import { ReactElement, useState, useEffect, useRef } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { Formik, Form, FormikProps } from 'formik';
import { defaultForm, getSubmissionPayload, iThirdPartyFormValues, ThirdPartySchema, thirdPartyMetaData } from '@services/ThirdPartyService';
import { FormContainer } from '@pages';
import { FormNavFooter, FormLoading, ProgressBar, Alert } from '@components';
import { getCountryList, getTooltips } from '@api';
import { navigateToError, navToError } from '@utils';
import { iToolTips, iCountry } from '@interfaces/interfaces';
import ReactGA from 'react-ga';
import ReCAPTCHA from 'react-google-recaptcha';
import ErrorContent from '@components/Content/AlertContent/ErrorContent';
import { TPStep1, TPStep2, TPStep3, TPStep4, TPStep5, TPStep6, TPStep7, TPErrorPage } from '.';
import ThirdPartyContext from '../VicFinesFormContext';
import IssueIcon from '../../../images/issue.svg';
import styles from './ThirdParty.module.scss';

function ThirdParty(): ReactElement {
  const { REACT_APP_SITECORE_API_ENDPOINT: API_URL, REACT_APP_PATH_THIRD_PARTY: PATH_THIRD_PARTY, REACT_APP_RECAPTCHA_SITE_KEY } = process.env;
  const [loading, setLoading] = useState(true);
  const [countryList, setCountryList] = useState<iCountry[]>([]);
  const [tooltips, setTooltips] = useState<iToolTips>({} as iToolTips);
  const [activeError, setActiveError] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [isCaptchaSuccessful, setIsCaptchaSuccess] = useState(false);
  const formikValuesRef = useRef({} as FormikProps<iThirdPartyFormValues>);
  const handleRecaptchaChange = (response: string | null) => {
    // Set the reCAPTCHA response in the state
    setIsCaptchaSuccess(true);
    formikValuesRef.current.setFieldValue('recaptchaResponse', response || '');
  };

  useEffect(() => {
    setIsCaptchaSuccess(false);
    const fetchData = async (): Promise<void> => {
      try {
        const [countryResults, tooltipResults] = await Promise.all([getCountryList(), getTooltips()]);
        setCountryList(countryResults);
        setTooltips(tooltipResults);
        setLoading(false);
      } catch (error) {
        navigateToError();
      }
    };
    fetchData();
  }, []);
  const formMandatoryFields = [
    'WhatDoesThisAuthorisationCover',
    'WhatFinesDoesThisAuthorisationCover',
    'HowLongWillThisAuthorisationLast',
    'WhoAreYouNominating',
    'IndividualContactNumber',
  ];
  const formSubmissionValidation = (values: iThirdPartyFormValues): boolean => formMandatoryFields.every((fieldName) => values[fieldName]);
  const currentStep = location.pathname === `${PATH_THIRD_PARTY}` ? 'step1' : `${location.pathname.split('/').slice(-1)}`;
  const displayErrorsCount = (errorCount: number, touchedFields: number): void => {
    if (touchedFields === 0) {
      setActiveError(true);
    } else if (errorCount > 0) {
      setActiveError(true);
    } else {
      setActiveError(false);
    }
  };
  const submitForm = async (values: iThirdPartyFormValues): Promise<boolean> => {
    setLoading(true);
    const submitValidation = formSubmissionValidation(values);
    if (!submitValidation) {
      setLoading(false);
      return false;
    }
    const submissionNumber = 'SubmissionNumber';
    const formSubmission = getSubmissionPayload(values, submissionNumber);
    const submitPayload = {
      data: JSON.stringify(formSubmission),
      obligationNo: values.OBLIGATION_NUMBER
    };

    const urlSubmission = `${API_URL}sitecore/ThirdParty/SubmitAuthorisation`;
    let submitResponse = false;
    submitResponse = await fetch(urlSubmission, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitPayload),
      })
      .then((formSubResponse) => {
        if (formSubResponse.status === 200) {
          formikValuesRef.current.setFieldValue('submitted', true);
          ReactGA.event({
            category: 'Third-party Form',
            action: 'Submit',
            label: 'Submit Third-party Form',
          });
          return formSubResponse.json();
        }
        formikValuesRef.current.setFieldValue('submissionError', formSubResponse.statusText);
        navToError(navigate, location);
        return false;
      }).then((response) => {
        if (response) {
          formikValuesRef.current.setFieldValue('submissionNo', response.receiptNo); // get submission number from API response
          return true;
        }
        return false;
      }).catch((error) => {
        formikValuesRef.current.setFieldValue('submissionError', error);
        navToError(navigate, location);
        return false;
      })
      .finally(() => {
        setLoading(false);
      });
      if (!submitResponse) return false;
      return true;
  };
  return (
    <Formik
      initialValues={defaultForm()}
      innerRef={formikValuesRef}
      validationSchema={ThirdPartySchema}
      onSubmit={(values: iThirdPartyFormValues) => {
        submitForm(values);
      }}>
      {({ values }) => (
        <ThirdPartyContext.Provider value={{ countryList, setCountryList, tooltips, setTooltips }}>
          <FormContainer title="Third Party">
            {loading && <FormLoading />}
            {!loading && (
              <Form>
                {!location.pathname.includes('error') && <ProgressBar formMetaData={thirdPartyMetaData} />}
                {activeError && !location.pathname.includes('error') && (
                  <Alert title="Sorry there is a problem" icon={IssueIcon} color="red" message={<ErrorContent />} theme="error" />
                )}
                <Routes>
                  <Route path="/step1" element={<TPStep1 />} />
                  <Route path="/step2" element={<TPStep2 />} />
                  <Route path="/step3" element={<TPStep3 />} />
                  <Route path="/step4" element={<TPStep4 />} />
                  <Route path="/step5" element={<TPStep5 />} />
                  <Route path="/step6" element={<TPStep6 />} />
                  <Route path="/step7" element={<TPStep7 />} />
                  <Route path="/error" element={<TPErrorPage />} />
                  <Route path="/" element={<TPStep1 />} />
                </Routes>
                {
                  (location.pathname === `${PATH_THIRD_PARTY}/step6`)
                  && (
                  <ReCAPTCHA
                    className={styles.recaptcha}
                    sitekey={`${REACT_APP_RECAPTCHA_SITE_KEY}`}
                    onChange={handleRecaptchaChange}
                    onExpired={() => setIsCaptchaSuccess(false)}
                    onErrored={() => setIsCaptchaSuccess(false)}
              />
)
                }
                {!(location.pathname === `${PATH_THIRD_PARTY}/step7` || location.pathname === `${PATH_THIRD_PARTY}/error`) && (
                  <FormNavFooter
                    totalPages={7}
                    submitPage={6}
                    submit={async () => submitForm(values)}
                    tooltips={tooltips}
                    displayErrorsCount={displayErrorsCount}
                    pageValidationKeys={thirdPartyMetaData.pagesSegments[currentStep].validationKeys(values)}
                    firstPageNo={1}
                    isDisabled={!isCaptchaSuccessful}
                  />
                )}
              </Form>
            )}
          </FormContainer>
        </ThirdPartyContext.Provider>
      )}
    </Formik>
  );
}

export default ThirdParty;
